import { useCallback, useEffect, useState, useMemo } from "react";

import { useLocation, useNavigate } from "react-router-dom";

import { useBxpWebStargateClient } from "@/libraries/bxp-web-stargate-client/src";
import { checkFeatureFlagGate } from "@/utils/feature-flags/check-feature-flag";
import { featureFlags } from "@/utils/feature-flags/feature-flag-manifest";

import { useFastshiftDispatch, useFastshiftSelector } from "../../../state";
import * as StateActions from "../../../state/actions";
import { useRoutes } from "../../hooks";
import {
    BASE_URL,
    FASTSHIFT_DOMAINS,
    FASTSHIFT_BASE_URL,
    FASTSHIFT_AUTH_REDIRECT_URL
} from "../../routes/constants";
import { removeTrailingSlash } from "../../routes/utils";

export enum UserAccessValidationState {
    LOADING_PORTAL_ELIGIBILITY = "LOADING_PORTAL_ELIGIBILITY",
    LOADING_USER_PERMISSIONS = "LOADING_USER_PERMISSIONS",
    VALIDATION_FAIL_NO_ACCESS_TO_SELECTED_ROUTE = "VALIDATION_FAIL_NO_ACCESS_TO_SELECTED_ROUTE",
    VALIDATION_FAIL_INELIGIBLE_FOR_PORTAL = "VALIDATION_FAIL_INELIGIBLE_FOR_PORTAL",
    VALIDATION_SUCCESS = "VALIDATION_SUCCESS"
}

export const usePortalAuthenticationAndAccessControl = () => {
    const stargateClient = useBxpWebStargateClient();
    const {
        isValidFastshiftRoute,
        domainId,
        paths,
        isValidFastshiftPartnerRoute
    } = useRoutes();
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const dispatch = useFastshiftDispatch();
    const { data: userDetails, error: userDetailsError } = useFastshiftSelector(
        (state) => state.userDetails
    );
    const { data: customerDomainData, error: customerDomainError } =
        useFastshiftSelector((state) => state.customerDomain);
    const { data: domainsForPartnerAccess } = useFastshiftSelector(
        (state) => state.customerDomains
    );
    const [userAccessState, setUserAccessState] =
        useState<UserAccessValidationState>(
            UserAccessValidationState.LOADING_PORTAL_ELIGIBILITY
        );

    const isDisableAuthHookForTestingOnlyEnabled = checkFeatureFlagGate(
        featureFlags.sxpFastshiftPortalDisableAuthHookTestingOnly
    );

    const strippedPathname = useMemo(
        () => removeTrailingSlash(pathname),
        [pathname]
    );

    const isPartnerOrInternal =
        userDetails?.userType === "PARTNER" ||
        userDetails?.userType === "INTERNAL";

    const pageDomainIsPresentInPartnerSelectionTable = useMemo(
        () =>
            domainsForPartnerAccess?.data?.some(
                (accessibleDomain) =>
                    accessibleDomain.customerDomainId === domainId
            ),
        [domainsForPartnerAccess, domainId]
    );

    const shouldShowSideNav: boolean = useMemo(() => {
        return (
            [
                UserAccessValidationState.VALIDATION_SUCCESS,
                UserAccessValidationState.LOADING_USER_PERMISSIONS
            ].includes(userAccessState) &&
            isValidFastshiftRoute(pathname) &&
            strippedPathname !== paths.home
        );
    }, [
        userAccessState,
        isValidFastshiftRoute,
        pathname,
        strippedPathname,
        paths.home
    ]);

    const isOnHomeRoute: boolean = useMemo(
        () => strippedPathname === paths.home,
        [strippedPathname, paths.home]
    );

    const resolvesIfUserIsAuthenticated = useCallback(async () => {
        return stargateClient.me({});
    }, [stargateClient]);

    const redirectToHomeRoute = () => {
        if (
            strippedPathname !== paths.home &&
            !isDisableAuthHookForTestingOnlyEnabled
        ) {
            navigate(paths.home);
        }
    };

    const redirectIneligibleUserToHome = () => {
        setUserAccessState(
            UserAccessValidationState.VALIDATION_FAIL_INELIGIBLE_FOR_PORTAL
        );
        redirectToHomeRoute();
    };

    // Trigger appropriate checks on page route change
    useEffect(() => {
        // if present, user has already passed portal eligibility checks
        if (userDetails) {
            // Avoid flickering/loading state when navigating between routes after success
            if (
                userAccessState !== UserAccessValidationState.VALIDATION_SUCCESS
            ) {
                setUserAccessState(
                    UserAccessValidationState.LOADING_USER_PERMISSIONS
                );
            }
            return;
        }

        // if previously shown to be ineligible for portal
        if (userDetailsError) {
            redirectIneligibleUserToHome();
            return;
        }

        setUserAccessState(
            UserAccessValidationState.LOADING_PORTAL_ELIGIBILITY
        );
        // should only run when user changes page
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname]);

    // Portal Eligibility: Check User is Authenticated
    useEffect(() => {
        if (
            userAccessState !==
            UserAccessValidationState.LOADING_PORTAL_ELIGIBILITY
        ) {
            return;
        }

        // if user details is not present, ensure user is authenticated and fetch details
        resolvesIfUserIsAuthenticated()
            .then(() => {
                // Retrieve /user details
                dispatch(StateActions.getUserDetails(stargateClient));
            })
            .catch(() => {
                // Redirect to Identity to login user
                const currentDomain = window.location.hostname;
                // If fastshift domain - use new fastshift login auth redirect url
                if (FASTSHIFT_DOMAINS.includes(currentDomain)) {
                    const fastshiftLoginAuthContinueUrl = encodeURIComponent(
                        `${FASTSHIFT_BASE_URL}${FASTSHIFT_AUTH_REDIRECT_URL}`
                    );
                    const loginUrl = `${process.env["NEXT_PUBLIC_IDENTITY_BASE_URL"]}/login?continue=${fastshiftLoginAuthContinueUrl}`;
                    window.location.href = loginUrl;
                } else {
                    const loginUrl = `${
                        process.env["NEXT_PUBLIC_IDENTITY_BASE_URL"]
                    }/login?continue=${encodeURIComponent(
                        `${BASE_URL}${strippedPathname}`
                    )}`;
                    window.location.href = loginUrl;
                }
            });
        // should only run Portal Eligibility check is initiated
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userAccessState]);

    // Portal Eligibility: Check User is Eligible
    useEffect(() => {
        if (
            userAccessState !==
            UserAccessValidationState.LOADING_PORTAL_ELIGIBILITY
        ) {
            return;
        }

        // still resolving details
        if (!userDetails && !userDetailsError) {
            return;
        }

        // if ineligible for portal
        if (userDetailsError) {
            redirectIneligibleUserToHome();
            return;
        }

        // at this point, permissions on specific page may be checked
        setUserAccessState(UserAccessValidationState.LOADING_USER_PERMISSIONS);

        // if on EAP home route, redirect to Milestone 2 home route
        if (strippedPathname === paths.eapHome) {
            redirectToHomeRoute();
            return;
        }

        // should only run when /user endpoint resolves during eligibility check
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userDetails, userDetailsError]);

    // User Permissions: Page Check
    // eslint-disable-next-line complexity
    useEffect(() => {
        // Wait until portal eligibility (user details) is resolved
        if (!userDetails && !userDetailsError) {
            return;
        }

        // If user is ineligible for portal, do not perform route-level checks
        if (userDetailsError) {
            return;
        }

        // any authenticated user may access home /fastshift route
        if (strippedPathname === paths.home) {
            setUserAccessState(UserAccessValidationState.VALIDATION_SUCCESS);
            return;
        }

        // if not on a valid fastshift route, immediate 404
        if (!isValidFastshiftRoute(strippedPathname)) {
            setUserAccessState(
                UserAccessValidationState.VALIDATION_FAIL_NO_ACCESS_TO_SELECTED_ROUTE
            );
            return;
        }

        // check partner-specific routes (no domainId)
        if (!domainId) {
            if (
                isPartnerOrInternal &&
                isValidFastshiftPartnerRoute(strippedPathname)
            ) {
                setUserAccessState(
                    UserAccessValidationState.VALIDATION_SUCCESS
                );
            } else {
                setUserAccessState(
                    UserAccessValidationState.VALIDATION_FAIL_NO_ACCESS_TO_SELECTED_ROUTE
                );
            }
            return;
        }

        // domain-scoped routes:
        // customer may access their own page without waiting for domain API
        if (
            userDetails?.userType === "CUSTOMER" &&
            domainId === userDetails.userDomain.domainId
        ) {
            setUserAccessState(UserAccessValidationState.VALIDATION_SUCCESS);
            return;
        }

        // partners/internal may access domains from selection table
        if (isPartnerOrInternal) {
            if (pageDomainIsPresentInPartnerSelectionTable === true) {
                setUserAccessState(
                    UserAccessValidationState.VALIDATION_SUCCESS
                );
                return;
            }
        }

        // still pending domain info -> keep current state (likely LOADING_USER_PERMISSIONS)
        if (!customerDomainData && !customerDomainError) {
            return;
        }

        // if /domain returns error, then user may not access page
        if (customerDomainError) {
            setUserAccessState(
                UserAccessValidationState.VALIDATION_FAIL_NO_ACCESS_TO_SELECTED_ROUTE
            );
            return;
        }

        // any user may access a page where /domain returns valid response
        if (
            customerDomainData &&
            customerDomainData.customerDomainId === domainId
        ) {
            setUserAccessState(UserAccessValidationState.VALIDATION_SUCCESS);
            return;
        }

        // default: no access
        setUserAccessState(
            UserAccessValidationState.VALIDATION_FAIL_NO_ACCESS_TO_SELECTED_ROUTE
        );
    }, [
        strippedPathname,
        domainId,
        userDetails,
        customerDomainData,
        customerDomainError,
        userDetailsError,
        isPartnerOrInternal,
        isValidFastshiftRoute,
        isValidFastshiftPartnerRoute,
        paths,
        pageDomainIsPresentInPartnerSelectionTable
    ]);

    return {
        userAccessState,
        shouldShowSideNav,
        isPartnerOrInternal,
        isOnHomeRoute
    };
};
